import type { FC, ReactNode } from 'react'

import Link from 'next/link'

/**
 * One MTA market category card.
 *
 * A single coloured panel with a thick black outline and an offset shadow:
 * uppercase title at the top, the category artwork in the middle, a short
 * description at the bottom. Every card in a row shares the same dimensions,
 * padding, radius, typography and shadow — only the colour, artwork, title,
 * description and link change.
 *
 * Styling lives in the `.mkt-*` block of globals.css.
 */

export type MarketCardTone =
    | 'amber'
    | 'violet'
    | 'red'
    | 'sky'
    | 'lime'
    | 'coral'
    | 'mint'
    | 'orange'
    | 'lavender'
    | 'cobalt'
    | 'albania'

export interface MarketCategoryCardProps {
    /** Uppercase title at the top of the card. */
    title: string
    /** Short line at the bottom of the card. */
    description: string
    /** Artwork rendered in the middle of the card. */
    icon: ReactNode
    href: string
    /** Card colour. */
    background: MarketCardTone
}

export const MarketCategoryCard: FC<MarketCategoryCardProps> = ({
    title,
    description,
    icon,
    href,
    background
}): ReactNode => (
    <Link className={`mkt-card mkt-${background}`} href={href}>
        <h3 className='mkt-card-title'>
            {title}
        </h3>

        <span className='mkt-art' aria-hidden='true'>
            {icon}
        </span>

        <p className='mkt-card-desc'>
            {description}
        </p>
    </Link>
)
